import { all, call, put, takeLatest } from 'redux-saga/effects';

import {
  getQBOAuth,
  getInvoiceList,
  getInvoiceLineList,
  getInvoiceDisputeHistoryList,
  getInvoiceDetail,
  getInvoicePDF,
  sendDispute,
  fetchInvoicePolicy,
  searchInvoiceNumber,
} from './APIs/invoice';

import types from '../constants/invoiceConstants';

import {
  setRedirectURLAction,
  setErrorAction,
  setInvoiceListAction,
  setInvoiceListErrorAction,
  setInvoiceLineListAction,
  setInvoiceLineListErrorAction,
  setInvoiceDisputeHistoryListAction,
  setInvoiceDisputeHistoryListErrorAction,
  setInvoiceDetailAction,
  setInvoiceDetailErrorAction,
  setDisputeSubmitSuccessAction,
  setDisputeSubmitErrorAction,
  getInvoiceDetailAction,
  getInvoiceLineListAction,
  getInvoiceDisputeHistoryListAction,
  setPDFLoadingAction,
  setPolicyAction,
  searchSpinnerAction,
} from '../actions/invoiceActions';
import { successNotif, failedNotif } from '../actions/notifActions';
import {
  downloadInvoicePdfBlob,
  buildInvoicePdfFilename,
  assertInvoicePdfBlob,
} from 'utils/invoicePdfDownload';
import { parseAxiosErrorMessage } from 'utils/parseAxiosErrorMessage';
import { getApiErrorMessage } from 'utils/apiError';

function* getQBOAuthSaga() {
  try {
    const res = yield getQBOAuth();
    const authUrl = typeof res === 'string' ? res : res?.url || res?.authUri;
    if (!authUrl || !`${authUrl}`.startsWith('http')) {
      throw new Error('Invalid QuickBooks authorization URL received from the server.');
    }
    yield put(setRedirectURLAction(authUrl));
  } catch (error) {
    const message = getApiErrorMessage(error, 'Unable to start QuickBooks connection.');
    yield put(failedNotif(message));
    yield put(setRedirectURLAction(null));
  }
}

function* onGetQBOAuth() {
  yield takeLatest(types.GET_QBOAUTH, getQBOAuthSaga);
}

function* getInvoiceListSaga({ payload }) {
  try {
    const res = yield getInvoiceList(payload);
    yield put(setInvoiceListAction(res));
  } catch (error) {
    const message = getApiErrorMessage(error, 'Unable to load invoices.');
    yield put(setInvoiceListErrorAction(message));
    yield put(failedNotif(message));
  }
}

function* onGetInvoiceList() {
  yield takeLatest(types.GET_INVOICE_LIST, getInvoiceListSaga);
}

function* getInvoiceLineListSaga({ payload }) {
  try {
    const res = yield getInvoiceLineList(payload);
    yield put(setInvoiceLineListAction(res));
  } catch (error) {
    const message = getApiErrorMessage(error, 'Unable to load invoice lines.');
    yield put(setInvoiceLineListErrorAction(message));
    yield put(failedNotif(message));
  }
}

function* onGetInvoiceLineList() {
  yield takeLatest(types.GET_INVOICE_LINE_LIST, getInvoiceLineListSaga);
}

function* getInvoiceDisputeHistoryListSaga({ payload }) {
  try {
    const res = yield getInvoiceDisputeHistoryList(payload);
    yield put(setInvoiceDisputeHistoryListAction(res));
  } catch (error) {
    const message = getApiErrorMessage(error, 'Unable to load dispute history.');
    yield put(setInvoiceDisputeHistoryListErrorAction(message));
    yield put(failedNotif(message));
  }
}

function* onGetInvoiceDisputeHistoryList() {
  yield takeLatest(types.GET_INVOICE_DISPUTE_HISTORY_LIST, getInvoiceDisputeHistoryListSaga);
}

function* getInvoiceDetailSaga({ payload }) {
  try {
    const res = yield getInvoiceDetail(payload);
    yield put(setInvoiceDetailAction(res));
  } catch (error) {
    yield put(setInvoiceDetailErrorAction());
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to load invoice detail.')));
  }
}

function* onGetInvoiceDetail() {
  yield takeLatest(types.GET_INVOICE_DETAIL, getInvoiceDetailSaga);
}

function* getInvoicePDFSaga({ payload }) {
  const invoiceId = payload?.invoiceId ?? payload;
  const invoiceMeta = payload?.invoice;
  try {
    yield put(setPDFLoadingAction(invoiceId));
    const blob = yield getInvoicePDF(invoiceId);
    const pdfBlob = yield call(assertInvoicePdfBlob, blob);
    downloadInvoicePdfBlob(
      pdfBlob,
      buildInvoicePdfFilename(invoiceMeta || { invoice_id: invoiceId }),
    );
    yield put(successNotif('Invoice PDF downloaded.'));
  } catch (error) {
    const message = yield call(parseAxiosErrorMessage, error, 'Unable to download invoice PDF.');
    yield put(failedNotif(message));
  } finally {
    yield put(setPDFLoadingAction(null));
  }
}

function* onGetInvoicePDF() {
  yield takeLatest(types.GET_INVOICE_PDF, getInvoicePDFSaga);
}

function* sendDisputeSaga({ payload }) {
  const invoiceId = payload?.[0]?.invoice_id;
  const hasClaimFiledReason =
    Array.isArray(payload) && payload.some((line) => line.reason === 'Claim Filed');

  try {
    const res = yield sendDispute(payload);

    if (invoiceId) {
      yield put(getInvoiceDetailAction(invoiceId));
      yield put(
        getInvoiceLineListAction({
          invoice_id: invoiceId,
          filter: { searchText: '' },
          sortOptions: {},
          offset: 0,
          limit: 12,
        }),
      );
      yield put(
        getInvoiceDisputeHistoryListAction({
          invoice_id: invoiceId,
          filter: { searchText: '' },
          sortOptions: {},
          offset: 0,
          limit: 12,
        }),
      );
    }

    yield put(setDisputeSubmitSuccessAction({ hasClaimFiledReason }));
    yield put(successNotif(typeof res === 'string' ? res : 'Dispute successfully sent to Leap.'));
  } catch (error) {
    const message = getApiErrorMessage(error, 'Unable to submit dispute.');
    yield put(setDisputeSubmitErrorAction());
    yield put(failedNotif(message));
  }
}

function* onSendDisputeToAdmin() {
  yield takeLatest(types.SEND_DISPUTE, sendDisputeSaga);
}

function* fetchInvoicePolicySaga({ payload }) {
  try {
    const res = yield fetchInvoicePolicy(payload);
    yield put(setPolicyAction(res));
  } catch (error) {
    yield put(
      failedNotif(getApiErrorMessage(error, 'Failed to load policy for this invoice line.')),
    );
  }
}

function* onFetchInvoicePolicy() {
  yield takeLatest(types.FETCH_INVOICE_POLICY, fetchInvoicePolicySaga);
}

function* searchInvoiceNumberSaga({ payload }) {
  try {
    yield put(searchSpinnerAction(true));
    const res = yield searchInvoiceNumber(payload);
    yield put(searchSpinnerAction(false));
    yield put(
      setInvoiceListAction({
        ...res,
        total: res.total ?? (Array.isArray(res.invoiceData) ? res.invoiceData.length : 0),
      }),
    );
  } catch (error) {
    yield put(searchSpinnerAction(false));
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to search invoices.')));
  }
}

function* onSearchInvoiceNumber() {
  yield takeLatest(types.SEARCH_INVOICE_NUMBER, searchInvoiceNumberSaga);
}

export default function* docSaga() {
  yield all([
    call(onGetQBOAuth),
    call(onGetInvoiceList),
    call(onGetInvoiceLineList),
    call(onGetInvoiceDisputeHistoryList),
    call(onGetInvoiceDetail),
    call(onGetInvoicePDF),
    call(onSendDisputeToAdmin),
    call(onFetchInvoicePolicy),
    call(onSearchInvoiceNumber),
  ]);
}
